import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import PointageModule from '../components/shared/PointageModule';
import MarquerFinJournee from '../components/MarquerFinJournee';
import { MaPerformance } from '../components/rh';
import MesDemandesPermission from '../components/employe/MesDemandesPermission';

// ── UTILS ──
const pct = (d, t) => t > 0 ? Math.round(d / t * 100) : 0;
const scol = s => s >= 8 ? '#66bb6a' : s >= 6 ? '#C9A84C' : '#ef8080';
const pcol = p => p >= 70 ? '#4caf50' : p >= 40 ? '#ff9800' : '#ef5350';

const PBar = ({ p, color, h = 5 }) => (
  <div style={{ background: 'rgba(255,255,255,.06)', borderRadius: 99, height: h, overflow: 'hidden' }}>
    <div style={{ width: `${p}%`, background: color || pcol(p), height: '100%', borderRadius: 99, transition: 'width .8s' }} />
  </div>
);

const Bdg = ({ children, cls }) => {
  const map = {
    bg: { background: 'rgba(67,160,71,0.15)', color: '#66bb6a' },
    bo: { background: 'rgba(251,140,0,0.15)', color: '#ffb74d' },
    br: { background: 'rgba(229,57,53,0.15)', color: '#ef8080' },
    bb: { background: 'rgba(21,101,192,0.15)', color: '#64b5f6' },
    bgold: { background: 'rgba(201,168,76,0.12)', color: '#C9A84C' },
  };
  return <span style={{ display: 'inline-block', fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 99, ...map[cls] }}>{children}</span>;
};

const sLabel = s => ({
  atteint: <Bdg cls="bg">✅ Atteint</Bdg>,
  en_cours: <Bdg cls="bgold">🔄 En cours</Bdg>,
  non_atteint: <Bdg cls="br">❌ Non atteint</Bdg>,
  planifie: <Bdg cls="bb">📅 Planifié</Bdg>,
  retard: <Bdg cls="br">🚨 Retard</Bdg>,
}[s] || s);

const PtCell = ({ s }) => {
  if (s === 'ok') return <div style={{ padding: '8px 10px', borderRadius: 6, textAlign: 'center', fontSize: 11, fontWeight: 600, background: 'rgba(67,160,71,.15)', color: '#66bb6a' }}>✓</div>;
  if (s === 'retard') return <div style={{ padding: '8px 10px', borderRadius: 6, textAlign: 'center', fontSize: 10, fontWeight: 600, background: 'rgba(251,140,0,.15)', color: '#ffb74d' }}>⏰</div>;
  return <div style={{ padding: '8px 10px', borderRadius: 6, textAlign: 'center', fontSize: 11, fontWeight: 600, background: 'rgba(229,57,53,.12)', color: '#ef8080' }}>✕</div>;
};

const Card = ({ children, style }) => (
  <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.06)', borderRadius: 11, padding: '16px 18px', marginBottom: 14, ...style }}>{children}</div>
);

const CTitle = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>{children}</div>
);

const Cbdg = ({ children, style }) => (
  <span style={{ fontSize: 9, color: 'rgba(255,255,255,.42)', background: 'rgba(255,255,255,.05)', padding: '2px 7px', borderRadius: 99, ...style }}>{children}</span>
);

// ── MODAL ──
function Modal({ id, title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#0d1825', border: '1px solid rgba(255,255,255,.06)', borderRadius: 12, padding: 22, width: 500, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 19, fontWeight: 600, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#667', fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Fin = (props) => (
  <input {...props} style={{ background: 'rgba(255,255,255,.05)', border: '1.5px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '9px 11px', color: '#fff', fontSize: 12, outline: 'none', width: '100%', ...props.style }} />
);
const Fsel = ({ children, ...props }) => (
  <select {...props} style={{ background: '#1a2a3a', border: '1.5px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '9px 11px', color: '#fff', fontSize: 12, outline: 'none', width: '100%', appearance: 'none', ...props.style }}>
    {children}
  </select>
);
const Fta = (props) => (
  <textarea {...props} style={{ background: 'rgba(255,255,255,.05)', border: '1.5px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '9px 11px', color: '#fff', fontSize: 12, outline: 'none', width: '100%', resize: 'vertical', ...props.style }} />
);
const Flbl = ({ children }) => <label style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: '#8a9bb0' }}>{children}</label>;
const BtnG = ({ children, onClick, style }) => (
  <button onClick={onClick} style={{ background: 'linear-gradient(135deg,#C9A84C,#8a6028)', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', ...style }}>{children}</button>
);
const BtnO = ({ children, onClick, style }) => (
  <button onClick={onClick} style={{ background: 'transparent', border: '1.5px solid rgba(255,255,255,.06)', borderRadius: 7, padding: '7px 14px', color: '#888', fontSize: 11, cursor: 'pointer', ...style }}>{children}</button>
);

// ── PANELS ──
const prioColor = { urgente: '#ef5350', haute: '#ff9800', normale: '#42a5f5', basse: '#888' };

function TachesPanel({ e, setTachesQuot, toggleTaskApi }) {
  const [modalJustif, setModalJustif] = useState(null);
  const [justifText, setJustifText] = useState('');
  const [justifNiveau, setJustifNiveau] = useState('100');
  const [preuveFile, setPreuveFile] = useState(null);
  const [modalDetails, setModalDetails] = useState(null);
  const [modalEdit, setModalEdit] = useState(null); // { id, titre, prio, duree, cat }

  // Garde-fou: s'assurer que tachesQuot existe
  const tachesQuot = e.tachesQuot || [];
  const done = tachesQuot.filter(t => t.done);
  const pending = tachesQuot.filter(t => !t.done);
  const dayPct = pct(done.length, tachesQuot.length);
  const color = pcol(dayPct);

  const toggleTask = (taskId) => {
    const t = tachesQuot.find(x => x.id === taskId);
    if (t.done) {
      setTachesQuot(prev => prev.map(tk => tk.id === taskId ? { ...tk, done: false, justif: null } : tk));
      toggleTaskApi(taskId);
    } else {
      setModalJustif({ taskId, titre: t.titre });
      setJustifText(''); setJustifNiveau('100'); setPreuveFile(null);
    }
  };

  const saveJustif = async () => {
    if (!justifText.trim()) { alert('Veuillez décrire ce que vous avez réalisé.'); return; }
    if (!preuveFile) { alert('Veuillez joindre un document de preuve avant de valider.'); return; }
    
    try {
      console.log('📤 Upload du document de preuve...', {
        taskId: modalJustif.taskId,
        fileName: preuveFile.name,
        fileSize: preuveFile.size
      });

      // Upload du document de preuve
      const uploadResponse = await api.tasks.uploadProof(modalJustif.taskId, preuveFile);
      console.log('✅ Upload réussi:', uploadResponse);
      
      // Marquer la tâche comme terminée via toggle (plus simple et plus fiable)
      const toggleResponse = await api.tasks.toggle(modalJustif.taskId);
      console.log('✅ Tâche marquée comme terminée:', toggleResponse);
      
      // Mettre à jour le state local
      setTachesQuot(prev => prev.map(tk => 
        tk.id === modalJustif.taskId 
          ? { ...tk, done: true, justif: justifText, document_preuve: uploadResponse.task?.document_preuve } 
          : tk
      ));
      
      setModalJustif(null);
      setPreuveFile(null);
      setJustifText('');
      
      alert('✅ Tâche validée avec succès !');
    } catch (error) {
      console.error('❌ Erreur lors de la validation:', error);
      alert('Erreur lors de la validation de la tâche: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const deleteTask = async (taskId) => {
    try { await api.tasks.delete(taskId); } catch {}
    setTachesQuot(prev => prev.filter(t => t.id !== taskId));
  };

  const saveEdit = async () => {
    if (!modalEdit?.titre?.trim()) return;
    try {
      await api.tasks.update(modalEdit.id, {
        titre: modalEdit.titre,
        priority: modalEdit.prio,
        duration: modalEdit.duree,
        category: modalEdit.cat,
        employee_id: modalEdit.employee_id,
      });
      setTachesQuot(prev => prev.map(t => t.id === modalEdit.id
        ? { ...t, titre: modalEdit.titre, prio: modalEdit.prio, duree: modalEdit.duree, cat: modalEdit.cat }
        : t
      ));
    } catch (err) { alert('Erreur : ' + err.message); }
    setModalEdit(null);
  };

  const mkTask = (t) => (
    <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
      <div onClick={() => toggleTask(t.id)}
        style={{ width: 18, height: 18, borderRadius: 5, border: t.done ? 'none' : '1.5px solid #445', background: t.done ? '#43a047' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, flexShrink: 0, cursor: 'pointer', marginTop: 1, color: '#fff' }}>
        {t.done ? '✓' : ''}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: '#ccc', textDecoration: t.done ? 'line-through' : 'none', lineHeight: 1.3 }}>{t.titre}</div>
        <div style={{ fontSize: 10, color: '#556', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 99, fontWeight: 600, background: `${prioColor[t.prio]}22`, color: prioColor[t.prio] }}>{t.prio.toUpperCase()}</span>
          <span>🗂️ {t.cat}</span>
          <span>⏱️ {t.duree}</span>
          {t.justif && <span style={{ fontSize: 9, color: '#66bb6a', background: 'rgba(67,160,71,.12)', padding: '1px 7px', borderRadius: 99 }}>📎 {t.justif.slice(0, 35)}{t.justif.length > 35 ? '...' : ''}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={() => setModalDetails(t)}
          style={{ fontSize: 9, padding: '3px 9px', border: '1px solid rgba(255,255,255,.06)', borderRadius: 5, background: 'transparent', color: '#64b5f6', cursor: 'pointer' }}>
          👁️ Voir
        </button>
        {!t.done && (
          <>
            <button onClick={() => setModalEdit({ id: t.id, titre: t.titre, prio: t.prio, duree: t.duree, cat: t.cat, employee_id: t.employee_id })}
              style={{ fontSize: 9, padding: '3px 9px', border: '1px solid rgba(255,255,255,.06)', borderRadius: 5, background: 'transparent', color: '#C9A84C', cursor: 'pointer' }}>
              ✏️ Modifier
            </button>
            <button onClick={() => { setModalJustif({ taskId: t.id, titre: t.titre }); setJustifText(''); setJustifNiveau('100'); setPreuveFile(null); }}
              style={{ fontSize: 9, padding: '3px 9px', border: '1px solid rgba(255,255,255,.06)', borderRadius: 5, background: 'transparent', color: '#778', cursor: 'pointer' }}>
              📎 Valider
            </button>
          </>
        )}
        <button onClick={() => deleteTask(t.id)}
          style={{ fontSize: 9, padding: '3px 9px', border: '1px solid rgba(255,255,255,.06)', borderRadius: 5, background: 'transparent', color: '#ef5350', cursor: 'pointer' }}>
          🗑️
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: `conic-gradient(${color} ${dayPct * 3.6}deg,rgba(255,255,255,.06) 0deg)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontWeight: 600, color, lineHeight: 1 }}>{dayPct}%</div>
            <div style={{ fontSize: 9, color: '#667', marginTop: 1 }}>accompli</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Progression journalière de {e.prenom}</div>
            <PBar p={dayPct} color={color} h={8} />
            <div style={{ fontSize: 10, color: '#667', marginTop: 6 }}>{done.length} accomplie{done.length > 1 ? 's' : ''} · {pending.length} restante{pending.length > 1 ? 's' : ''} · Total : {tachesQuot.length}</div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <CTitle>
            🔴 À accomplir <Cbdg>{pending.length} tâche{pending.length > 1 ? 's' : ''}</Cbdg>
          </CTitle>
          {pending.length ? pending.map(mkTask) : <div style={{ padding: 16, textAlign: 'center', color: '#445', fontSize: 12 }}>🎉 Toutes les tâches sont accomplies !</div>}
        </Card>
        <Card>
          <CTitle>✅ Accomplies <Cbdg>{done.length} tâche{done.length > 1 ? 's' : ''}</Cbdg></CTitle>
          {done.length ? done.map(mkTask) : <div style={{ padding: 16, textAlign: 'center', color: '#445', fontSize: 12 }}>Aucune tâche accomplie pour l'instant.</div>}
        </Card>
      </div>

      <Modal open={!!modalJustif} onClose={() => setModalJustif(null)} title="📎 Valider la tâche">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><Flbl>Tâche</Flbl><Fin value={modalJustif?.titre || ''} readOnly style={{ color: '#aaa', background: 'rgba(255,255,255,.03)' }} /></div>
          <div><Flbl>Ce que j'ai réalisé *</Flbl><Fta rows={3} placeholder="Décrivez concrètement ce que vous avez accompli..." value={justifText} onChange={e => setJustifText(e.target.value)} /></div>
          <div><Flbl>Niveau d'accomplissement</Flbl>
            <Fsel value={justifNiveau} onChange={e => setJustifNiveau(e.target.value)}>
              <option value="100">Complètement réalisée (100%)</option>
              <option value="75">Largement réalisée (75%)</option>
              <option value="50">Partiellement réalisée (50%)</option>
              <option value="25">Débutée (25%)</option>
            </Fsel>
          </div>
          <div>
            <Flbl>Document de preuve *</Flbl>
            <div
              onClick={() => document.getElementById('preuve-upload').click()}
              style={{ marginTop: 6, border: `2px dashed ${preuveFile ? 'rgba(67,160,71,.5)' : 'rgba(255,255,255,.15)'}`, borderRadius: 8, padding: '14px 12px', textAlign: 'center', cursor: 'pointer', background: preuveFile ? 'rgba(67,160,71,.06)' : 'rgba(255,255,255,.02)' }}>
              <input id="preuve-upload" type="file" style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={ev => setPreuveFile(ev.target.files[0] || null)} />
              {preuveFile
                ? <div style={{ fontSize: 12, color: '#66bb6a', fontWeight: 600 }}>Fichier joint : {preuveFile.name}</div>
                : <div style={{ fontSize: 11, color: '#667' }}>Cliquer pour joindre un fichier (PDF, Word, Excel, Image)</div>}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <BtnO onClick={() => setModalJustif(null)}>Annuler</BtnO>
          <BtnG onClick={saveJustif}>✅ Valider</BtnG>
        </div>
      </Modal>

      <Modal open={!!modalEdit} onClose={() => setModalEdit(null)} title="✏️ Modifier la tâche">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><Flbl>Intitulé *</Flbl><Fin value={modalEdit?.titre || ''} onChange={ev => setModalEdit(p => ({ ...p, titre: ev.target.value }))} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <div><Flbl>Priorité</Flbl>
              <Fsel value={modalEdit?.prio || 'normale'} onChange={ev => setModalEdit(p => ({ ...p, prio: ev.target.value }))}>
                <option value="urgente">🔴 Urgente</option>
                <option value="haute">🟠 Haute</option>
                <option value="normale">🔵 Normale</option>
              </Fsel>
            </div>
            <div><Flbl>Durée</Flbl>
              <Fsel value={modalEdit?.duree || '1h'} onChange={ev => setModalEdit(p => ({ ...p, duree: ev.target.value }))}>
                {['15 min', '30 min', '1h', '2h', 'Demi-journée'].map(d => <option key={d}>{d}</option>)}
              </Fsel>
            </div>
          </div>
          <div><Flbl>Catégorie</Flbl>
            <Fsel value={modalEdit?.cat || ''} onChange={ev => setModalEdit(p => ({ ...p, cat: ev.target.value }))}>
              {['Pilotage', 'Projet', 'Admin', 'Finance', 'RH', 'Autre'].map(c => <option key={c} value={c}>{c}</option>)}
            </Fsel>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <BtnO onClick={() => setModalEdit(null)}>Annuler</BtnO>
          <BtnG onClick={saveEdit}>✅ Enregistrer</BtnG>
        </div>
      </Modal>

      <Modal open={!!modalDetails} onClose={() => setModalDetails(null)} title="👁️ Détails de la tâche">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <Flbl>Titre de la tâche</Flbl>
            <div style={{ marginTop: 6, padding: '10px 12px', background: 'rgba(255,255,255,.03)', borderRadius: 7, fontSize: 13, color: '#fff', fontWeight: 500 }}>
              {modalDetails?.titre || '—'}
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <Flbl>Durée estimée</Flbl>
              <div style={{ marginTop: 6, padding: '10px 12px', background: 'rgba(255,255,255,.03)', borderRadius: 7, fontSize: 12, color: '#aaa' }}>
                ⏱️ {modalDetails?.duree || '—'}
              </div>
            </div>
            <div>
              <Flbl>Priorité</Flbl>
              <div style={{ marginTop: 6, padding: '10px 12px', background: 'rgba(255,255,255,.03)', borderRadius: 7, fontSize: 12 }}>
                <span style={{ padding: '2px 8px', borderRadius: 99, fontWeight: 600, background: `${prioColor[modalDetails?.prio]}22`, color: prioColor[modalDetails?.prio] }}>
                  {modalDetails?.prio?.toUpperCase() || '—'}
                </span>
              </div>
            </div>
          </div>

          <div>
            <Flbl>Catégorie</Flbl>
            <div style={{ marginTop: 6, padding: '10px 12px', background: 'rgba(255,255,255,.03)', borderRadius: 7, fontSize: 12, color: '#aaa' }}>
              🗂️ {modalDetails?.cat || '—'}
            </div>
          </div>

          <div>
            <Flbl>Description / Justificatif</Flbl>
            <div style={{ marginTop: 6, padding: '12px', background: 'rgba(255,255,255,.03)', borderRadius: 7, fontSize: 12, color: '#ccc', lineHeight: 1.6, minHeight: 80 }}>
              {modalDetails?.justif || 'Aucune description disponible pour cette tâche.'}
            </div>
          </div>

          <div>
            <Flbl>Statut</Flbl>
            <div style={{ marginTop: 6, padding: '10px 12px', background: 'rgba(255,255,255,.03)', borderRadius: 7, fontSize: 12 }}>
              {modalDetails?.done ? (
                <span style={{ color: '#66bb6a', fontWeight: 600 }}>✅ Tâche accomplie</span>
              ) : (
                <span style={{ color: '#ffb74d', fontWeight: 600 }}>⏳ En attente de validation</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <BtnG onClick={() => setModalDetails(null)}>Fermer</BtnG>
        </div>
      </Modal>
    </>
  );
}

function SemainePanel({ e }) {
  const tachesHebdo = e.tachesHebdo || [];
  const wkPct = pct(tachesHebdo.filter(t => t.done).length, tachesHebdo.length);
  return (
    <>
      <Card>
        <CTitle>📆 Accomplissement hebdomadaire <Cbdg style={{ color: pcol(wkPct) }}>{wkPct}% — {tachesHebdo.filter(t => t.done).length}/{tachesHebdo.length}</Cbdg></CTitle>
        <PBar p={wkPct} color={pcol(wkPct)} h={8} />
      </Card>
      <Card>
        <CTitle>📋 Tâches récurrentes de la semaine</CTitle>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {['Tâche', 'Récurrence', 'Statut', 'Justificatif'].map(h => (
              <th key={h} style={{ fontSize: 9, color: '#445', fontWeight: 600, letterSpacing: '.8px', textTransform: 'uppercase', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,.06)', textAlign: 'left' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {tachesHebdo.map(t => (
              <tr key={t.id}>
                <td style={{ fontSize: 11, color: '#fff', fontWeight: 500, padding: '9px 10px', borderBottom: '1px solid rgba(255,255,255,.03)' }}>{t.titre}</td>
                <td style={{ fontSize: 11, color: '#aaa', padding: '9px 10px', borderBottom: '1px solid rgba(255,255,255,.03)' }}>{t.jour}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid rgba(255,255,255,.03)' }}>{t.done ? <Bdg cls="bg">✅ Fait</Bdg> : <Bdg cls="br">⏳ Non fait</Bdg>}</td>
                <td style={{ fontSize: 10, color: '#778', padding: '9px 10px', borderBottom: '1px solid rgba(255,255,255,.03)', maxWidth: 200 }}>{t.justif || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function ObjectifsPanel({ e, onProgressionChange }) {
  const objectifs = e.objectifs || [];
  const objAtteints = objectifs.filter(o => o.statut === 'atteint').length;
  const scoreP = Math.round(objectifs.reduce((s, o) => s + (o.pct * o.poids / 100), 0));
  const [modalObj, setModalObj] = useState(null);
  const [nouvelleProgression, setNouvelleProgression] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);

  const ouvrirModal = (o) => {
    setModalObj(o);
    setNouvelleProgression(o.pct);
    setCommentaire('');
  };

  const sauvegarderProgression = async () => {
    setSaving(true);
    try {
      await onProgressionChange(modalObj.id, nouvelleProgression, commentaire);
      setModalObj(null);
    } catch (err) {
      alert('Erreur lors de la mise à jour : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 600, color: objAtteints === objectifs.length ? '#66bb6a' : '#C9A84C' }}>{objAtteints}/{objectifs.length}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.42)', marginTop: 4 }}>Objectifs atteints</div>
        </div>
        <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 600, color: '#C9A84C' }}>{scoreP}%</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.42)', marginTop: 4 }}>Score pondéré</div>
        </div>
        <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, padding: 14 }}>
          {[['atteint', '#66bb6a', '✅ Atteint'], ['en_cours', '#C9A84C', '🔄 En cours'], ['non_atteint', '#ef8080', '❌ Non atteint']].map(([s, c, l]) => {
            const n = objectifs.filter(o => o.statut === s).length;
            return n > 0 ? <div key={s} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}><span style={{ color: c }}>{l}</span><span style={{ fontWeight: 600, color: c }}>{n}</span></div> : null;
          })}
        </div>
      </div>
      <Card>
        <CTitle>🎯 Mes objectifs — {objectifs[0]?.periode || 'Mensuel'}</CTitle>
        {objectifs.length === 0 && (
          <div style={{ padding: '30px 0', textAlign: 'center', color: '#556', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎯</div>
            <div>Aucun objectif assigné pour le moment.</div>
          </div>
        )}
        <div style={{ display: 'grid', gap: 14 }}>
          {objectifs.map((o) => (
            <div 
              key={o.id || o.titre} 
              style={{ 
                padding: 18, 
                borderRadius: 10, 
                border: '1px solid rgba(255,255,255,.06)', 
                background: 'linear-gradient(135deg, rgba(201,168,76,.04) 0%, rgba(201,168,76,.01) 100%)',
                transition: 'all 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201,168,76,.3)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201,168,76,.08) 0%, rgba(201,168,76,.02) 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,.06)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201,168,76,.04) 0%, rgba(201,168,76,.01) 100%)';
              }}
            >
              {/* En-tête avec titre et badge statut */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4, lineHeight: 1.4 }}>
                    {o.titre}
                  </div>
                  {o.description && (
                    <div style={{ fontSize: 11, color: '#889', lineHeight: 1.5 }}>
                      {o.description}
                    </div>
                  )}
                </div>
                {sLabel(o.statut)}
              </div>

              {/* Indicateurs clés */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                gap: 10, 
                marginBottom: 12,
                padding: '12px 0',
                borderTop: '1px solid rgba(255,255,255,.04)',
                borderBottom: '1px solid rgba(255,255,255,.04)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#667', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Cible</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#C9A84C', fontFamily: "'Cormorant Garamond',serif" }}>{o.cible}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#667', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Actuel</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', fontFamily: "'Cormorant Garamond',serif" }}>{o.actuel}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#667', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Poids</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#C9A84C', fontFamily: "'Cormorant Garamond',serif" }}>{o.poids}%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#667', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Progression</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: pcol(o.pct), fontFamily: "'Cormorant Garamond',serif" }}>{o.pct}%</div>
                </div>
              </div>

              {/* Barre de progression améliorée */}
              <div style={{ marginBottom: 12 }}>
                <PBar p={o.pct} color={pcol(o.pct)} />
              </div>

              {/* Commentaire si présent */}
              {o.commentaire && (
                <div style={{ 
                  padding: 10, 
                  borderRadius: 6, 
                  background: 'rgba(255,255,255,.02)', 
                  border: '1px solid rgba(255,255,255,.04)',
                  marginBottom: 10
                }}>
                  <div style={{ fontSize: 9, color: '#889', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>💬 Commentaire</div>
                  <div style={{ fontSize: 11, color: '#aab', lineHeight: 1.5, fontStyle: 'italic' }}>
                    {o.commentaire}
                  </div>
                </div>
              )}

              {/* Bouton d'action */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => ouvrirModal(o)}
                  style={{ 
                    fontSize: 11, 
                    padding: '8px 16px', 
                    border: '1px solid rgba(201,168,76,.4)', 
                    borderRadius: 8, 
                    background: 'rgba(201,168,76,.12)', 
                    color: '#C9A84C', 
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(201,168,76,.2)';
                    e.currentTarget.style.borderColor = 'rgba(201,168,76,.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(201,168,76,.12)';
                    e.currentTarget.style.borderColor = 'rgba(201,168,76,.4)';
                  }}
                >
                  📊 Mettre à jour la progression
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Modal open={!!modalObj} onClose={() => setModalObj(null)} title="📊 Mettre à jour la progression">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Flbl>Objectif</Flbl>
            <Fin value={modalObj?.titre || ''} readOnly style={{ color: '#aaa', background: 'rgba(255,255,255,.03)' }} />
          </div>
          <div>
            <Flbl>Ma progression : {nouvelleProgression}%</Flbl>
            <input
              type="range" min="0" max="100" step="5"
              value={nouvelleProgression}
              onChange={e => setNouvelleProgression(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#C9A84C', marginTop: 8 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#667', marginTop: 4 }}>
              <span>0%</span><span>50%</span><span>100%</span>
            </div>
            <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,.04)', fontSize: 11, color: '#C9A84C', textAlign: 'center' }}>
              {nouvelleProgression >= 100 ? '✅ Objectif atteint' : nouvelleProgression >= 50 ? '🔄 En cours' : nouvelleProgression > 0 ? '🔄 Débuté' : '📅 Non commencé'}
              {' — statut mis à jour automatiquement par le système'}
            </div>
          </div>
          <div>
            <Flbl>Commentaire (optionnel)</Flbl>
            <Fta
              rows={3}
              placeholder="Décrivez ce que vous avez accompli..."
              value={commentaire}
              onChange={e => setCommentaire(e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <BtnO onClick={() => setModalObj(null)}>Annuler</BtnO>
          <BtnG onClick={sauvegarderProgression} style={{ opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Envoi...' : '✅ Confirmer'}
          </BtnG>
        </div>
      </Modal>
    </>
  );
}

// eslint-disable-next-line no-unused-vars
function ScorePanel({ e }) {
  const criteres = e.criteres || [];
  const sc = scol(e.score);
  const avg = criteres.length > 0 ? (criteres.reduce((s, c) => s + c.val, 0) / criteres.length).toFixed(1) : '0.0';
  const msg = e.score >= 9 ? 'Performance exceptionnelle. Continuer sur cette lancée.'
    : e.score >= 8 ? "Très bonne performance. Quelques axes d'amélioration."
      : e.score >= 7 ? 'Bonne performance. Renforcer ponctualité et tâches.'
        : e.score >= 6 ? 'Performance satisfaisante, des efforts nécessaires.'
          : '⚠️ Performance insuffisante. Entretien de suivi requis.';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      <Card style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 28 }}>
        <div style={{ width: 120, height: 120, borderRadius: '50%', border: `4px solid ${sc}`, background: `${sc}12`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 44, fontWeight: 600, color: sc, lineHeight: 1 }}>{e.score}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.42)' }}>/10</div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: sc, marginBottom: 5 }}>{e.scoreLabel}</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.42)', textAlign: 'center', maxWidth: 220, lineHeight: 1.5 }}>{msg}</div>
      </Card>
      <Card>
        <CTitle>📊 Critères d'évaluation</CTitle>
        {criteres.map(c => (
          <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
            <div style={{ flex: 1, fontSize: 12, color: '#aaa' }}>{c.label}</div>
            <div style={{ width: 130, flexShrink: 0 }}><PBar p={Math.round(c.val / 10 * 100)} color={c.color} /></div>
            <div style={{ fontSize: 12, fontWeight: 600, width: 36, textAlign: 'right', color: c.color }}>{c.val}/10</div>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: 10, background: 'rgba(255,255,255,.02)', borderRadius: 7, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, color: '#667' }}>Moyenne critères</span>
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, color: sc }}>{avg}/10</span>
        </div>
      </Card>
    </div>
  );
}

function PointagePanel({ e }) {
  const pointage = e.pointage || [];
  const retards = pointage.filter(p => p.statut === 'retard').length;
  const absences = pointage.filter(p => p.statut === 'absent').length;
  const ponct = pointage.length > 0 ? Math.round(pointage.filter(p => p.statut === 'ok').length / pointage.length * 100) : 0;
  const presence = pointage.length > 0 ? Math.round(pointage.filter(p => p.statut !== 'absent').length / pointage.length * 100) : 0;

  return (
    <>
      <Card>
        <CTitle>⏱️ Pointage — 7 derniers jours <Cbdg>Limite : {e.limite}</Cbdg></CTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 16 }}>
          {e.pointage.map((p, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 8, color: '#445', marginBottom: 5 }}>{p.date.split(' ')[0]}</div>
              <PtCell s={p.statut} />
              <div style={{ fontSize: 8, color: '#556', marginTop: 4 }}>{p.arr}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 10 }}>
          <span style={{ color: '#66bb6a' }}>✓ À l'heure : {e.pointage.filter(p => p.statut === 'ok').length}</span>
          <span style={{ color: '#ffb74d' }}>⏰ Retards : {retards}</span>
          <span style={{ color: '#ef8080' }}>✕ Absences : {absences}</span>
          <span style={{ color: '#C9A84C' }}>📈 Ponctualité : {ponct}%</span>
        </div>
      </Card>
      <Card>
        <CTitle>📊 Synthèse de présence</CTitle>
        {[["✅ Jours à l'heure", e.pointage.filter(p => p.statut === 'ok').length, '#66bb6a'],
        ['⏰ Retards', retards, '#ffb74d'],
        ['❌ Absences', absences, '#ef8080'],
        ['📈 Taux présence', presence + '%', '#C9A84C']
        ].map(([l, v, c]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
            <span style={{ fontSize: 12, color: '#aaa' }}>{l}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: c }}>{v}</span>
          </div>
        ))}
      </Card>
    </>
  );
}



// Fonction RapportPanel commentée temporairement
/*
function RapportPanel({ e }) {
  const done = e.tachesQuot.filter(t => t.done);
  const dayPct = pct(done.length, e.tachesQuot.length);
  const wkPct = pct(e.tachesHebdo.filter(t => t.done).length, e.tachesHebdo.length);
  const objAtteints = e.objectifs.filter(o => o.statut === 'atteint').length;
  const retards = e.pointage.filter(p => p.statut === 'retard').length;
  const absences = e.pointage.filter(p => p.statut === 'absent').length;
  const ponct = Math.round(e.pointage.filter(p => p.statut === 'ok').length / e.pointage.length * 100);
  const sc = scol(e.score);

  const recText = e.score >= 9
    ? `Performance exceptionnelle. ${e.prenom} ${e.nom} démontre un niveau d'excellence remarquable. Recommandation : valorisation via prime de performance et confier des responsabilités élargies.`
    : e.score >= 8
    ? `Très bonne performance. ${e.prenom} délivre un travail de qualité avec une forte fiabilité. Recommandation : maintenir le cap et travailler les quelques axes identifiés.`
    : e.score >= 7
    ? `Bonne performance. ${e.prenom} est un collaborateur sérieux avec des marges de progression. Recommandation : accompagnement sur la ponctualité et l'accomplissement complet des tâches quotidiennes.`
    : e.score >= 6
    ? `Performance satisfaisante avec réserves. Des efforts notables mais des insuffisances persistent. Recommandation : entretien de suivi mensuel + plan d'amélioration formalisé.`
    : `Performance insuffisante — Action requise. La situation de ${e.prenom} nécessite une intervention managériale urgente. Recommandation : entretien disciplinaire + plan de redressement avec objectifs mesurables.`;

  return (
    <>
      <Card>
        <CTitle>
          📄 Rapport de performance — {e.prenom} {e.nom} <Cbdg>Juin 2026</Cbdg>
          <BtnO onClick={() => { alert(`📥 Export PDF du rapport de ${e.prenom} ${e.nom} — Ouverture de l'impression...`); window.print(); }} style={{ fontSize: 10, padding: '4px 10px' }}>📥 PDF</BtnO>
        </CTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, color: '#C9A84C', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>📋 Identification</div>
            {[['Nom complet', `${e.prenom} ${e.nom}`], ['Poste', e.poste], ['Département', e.dept], ['Filiale', e.filiale], ['Contrat', e.contrat], ["Date d'entrée", e.entree]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize: 11, color: '#667' }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#C9A84C', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>📈 Performance</div>
            {[['Score global', `${e.score}/10 — ${e.scoreLabel}`], ['Tâches jour', `${dayPct}%`], ['Tâches semaine', `${wkPct}%`], ['Objectifs atteints', `${objAtteints}/${e.objectifs.length}`], ['Ponctualité 7j', `${ponct}%`], ['Retards', `${retards} jour(s)`], ['Absences', `${absences} jour(s)`]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize: 11, color: '#667' }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#C9A84C' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <CTitle>💪 Points forts</CTitle>
          {e.criteres.filter(c => c.val >= 8).length
            ? e.criteres.filter(c => c.val >= 8).map(c => (
              <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize: 13 }}>✅</span>
                <span style={{ fontSize: 12, color: '#aaa' }}>{c.label} — <strong style={{ color: '#66bb6a' }}>{c.val}/10</strong></span>
              </div>
            ))
            : <div style={{ fontSize: 11, color: '#445', padding: 12 }}>Aucun critère ≥ 8 cette période.</div>}
        </Card>
        <Card>
          <CTitle>🔧 Axes d'amélioration</CTitle>
          {e.criteres.filter(c => c.val < 7).length
            ? e.criteres.filter(c => c.val < 7).map(c => (
              <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize: 13 }}>⚠️</span>
                <span style={{ fontSize: 12, color: '#aaa' }}>{c.label} — <strong style={{ color: '#ef8080' }}>{c.val}/10</strong></span>
              </div>
            ))
            : <div style={{ fontSize: 11, color: '#66bb6a', padding: 12 }}>✅ Excellente performance sur tous les critères !</div>}
        </Card>
      </div>
      <Card>
        <CTitle>💬 Recommandation managériale</CTitle>
        <div style={{ padding: 14, background: 'rgba(255,255,255,.02)', borderRadius: 8, borderLeft: `3px solid ${sc}`, fontSize: 12, color: '#aaa', lineHeight: 1.7 }}>{recText}</div>
      </Card>
    </>
  );
}
*/

function ProjetsPanel({ projets }) {
  const statusColor = { en_cours: '#42a5f5', planification: '#C9A84C', production: '#ff9800', termine: '#66bb6a', annule: '#ef5350', negociation: '#ab47bc' };
  const statusLabel = { en_cours: 'En cours', planification: 'Planification', production: 'Production', termine: 'Terminé', annule: 'Annulé', negociation: 'Négociation' };

  if (projets.length === 0) {
    return (
      <Card>
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#445' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 13 }}>Vous n'êtes assigné à aucun projet pour le moment.</div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 14 }}>
        {[
          [projets.length, 'Projets assignés', '#C9A84C'],
          [projets.filter(p => ['en_cours','production','negociation'].includes(p.status)).length, 'En cours', '#42a5f5'],
          [projets.filter(p => p.status === 'termine').length, 'Terminés', '#66bb6a'],
        ].map(([v, l, c]) => (
          <div key={l} style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 600, color: c }}>{v}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.42)', marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>
      {projets.map(p => {
        const sc = statusColor[p.status] || '#888';
        const av = p.progress || 0;
        return (
          <Card key={p.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: '#667' }}>
                  {p.filiale?.nom || '—'}{p.departement?.nom ? ' · ' + p.departement.nom : ''}
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: sc + '22', color: sc }}>
                {statusLabel[p.status] || p.status}
              </span>
            </div>
            {p.description && (
              <div style={{ fontSize: 11, color: '#778', marginBottom: 12, lineHeight: 1.5 }}>{p.description}</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              {[
                ['Budget', p.budget ? Number(p.budget).toLocaleString('fr-FR') + ' FCFA' : '—'],
                ['Deadline', p.end_date ? new Date(p.end_date).toLocaleDateString('fr-FR') : '—'],
                ['Chef de projet', p.owner ? (p.owner.prenom + ' ' + (p.owner.nom || '')) : '—'],
              ].map(([l, v]) => (
                <div key={l} style={{ background: 'rgba(255,255,255,.03)', borderRadius: 7, padding: '8px 10px' }}>
                  <div style={{ fontSize: 9, color: '#556', marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#ccc' }}>{v}</div>
                </div>
              ))}
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#667', marginBottom: 5 }}>
                <span>Avancement</span>
                <span style={{ color: av >= 70 ? '#66bb6a' : av >= 40 ? '#ff9800' : '#ef5350', fontWeight: 600 }}>{av}%</span>
              </div>
              <PBar p={av} />
            </div>
          </Card>
        );
      })}
    </>
  );
}

// ── MODAL NOUVELLE TÂCHE ──
function ModalNewTask({ open, onClose, onSave }) {
  const [titre, setTitre] = useState('');
  const [prio, setPrio] = useState('normale');
  const [duree, setDuree] = useState('1h');
  const [cat, setCat] = useState('');

  const save = () => {
    if (!titre.trim()) { alert('Intitulé requis.'); return; }
    onSave({ titre, prio, duree, cat: cat || 'Autre' });
    setTitre(''); setCat('');
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="➕ Ajouter une tâche">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div><Flbl>Intitulé *</Flbl><Fin placeholder="Décrivez la tâche..." value={titre} onChange={e => setTitre(e.target.value)} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
          <div><Flbl>Priorité</Flbl>
            <Fsel value={prio} onChange={e => setPrio(e.target.value)}>
              <option value="urgente">🔴 Urgente</option>
              <option value="haute">🟠 Haute</option>
              <option value="normale">🔵 Normale</option>
            </Fsel>
          </div>
          <div><Flbl>Durée estimée</Flbl>
            <Fsel value={duree} onChange={e => setDuree(e.target.value)}>
              {['15 min', '30 min', '1h', '2h', 'Demi-journée'].map(d => <option key={d}>{d}</option>)}
            </Fsel>
          </div>
        </div>
        <div><Flbl>Catégorie</Flbl>
          <Fsel value={cat} onChange={e => setCat(e.target.value)}>
            <option value="">-- Sélectionner --</option>
            <option value="Pilotage">📊 Pilotage</option>
            <option value="Projet">📋 Projet</option>
            <option value="Admin">🗂️ Admin</option>
            <option value="Finance">💰 Finance</option>
            <option value="RH">👥 RH</option>
            <option value="Autre">📌 Autre</option>
          </Fsel>
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <BtnO onClick={onClose}>Annuler</BtnO>
        <BtnG onClick={save}>✅ Ajouter</BtnG>
      </div>
    </Modal>
  );
}

// ── MAIN ──
const TABS = [
  { id: 'taches', label: '✅ Tâches du jour' },
  { id: 'semaine', label: '📆 Tâches semaine' },
  { id: 'objectifs', label: '🎯 Objectifs' },
  { id: 'score', label: '⭐ Score & Performance' },
  { id: 'pointage', label: '⏱️ Pointage' },
  { id: 'projets', label: '📋 Mes Projets' },
  { id: 'permissions', label: '📝 Mes Permissions' },
  { id: 'documents', label: '📁 Documents' },
];

// ── Normaliseur de tâches API → format composant ──
function normalizeTask(t) {
  return {
    id: t.id,
    titre: t.titre,
    cat: t.category || 'Autre',
    prio: t.priority || 'normale',
    duree: t.duration || '—',
    done: !!t.done,
    justif: t.description || null,
    date_tache: t.date_tache || null,
    type_periode: t.type_periode || 'quotidienne',
    _hebdo: t.type_periode === 'hebdomadaire',
    employee_id: t.employee_id || null,
  };
}

// ── Normaliseur de pointage API → format composant ──
function normalizePointage(p) {
  return {
    date: p.date ? new Date(p.date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '—',
    arr: p.heure || '—',
    dep: p.heure_depart || '—',
    statut: p.statut || 'absent',
  };
}

// ── Normaliseur objectif API → format composant ──
function normalizeObjectif(o) {
  return {
    id: o.id,
    titre: o.titre,
    description: o.description || '',
    periode: o.periode || 'Mensuel',
    cible: o.cible || '—',
    actuel: String(o.pourcentage || 0) + '%',
    pct: o.pourcentage || o.progression || 0,
    poids: o.poids || 20,
    statut: o.statut || 'en_cours',
    commentaire: o.commentaire || '',
  };
}

// ── Normaliseur critère API → format composant ──
function normalizeCritere(c) {
  const val = parseFloat(c.valeur) || 0;
  const color = val >= 8 ? '#66bb6a' : val >= 6 ? '#C9A84C' : '#ef8080';
  return { label: c.label, val, color };
}

export default function TdbIndividuelPage() {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();
  const [activeTab, setActiveTab] = useState('taches');
  const [showNewTask, setShowNewTask] = useState(false);
  const [loading, setLoading] = useState(true);

  // État employé courant depuis le back
  const [e, setE] = useState(null);
  const [tachesQuot, setTachesQuot] = useState([]);
  const [tachesHebdo, setTachesHebdo] = useState([]);
  const [objectifs, setObjectifs] = useState([]);
  const [pointageHist, setPointageHist] = useState([]);
  const [criteres, setCriteres] = useState([]);
  const [projets, setProjets] = useState([]);

  // Chargement initial depuis l'API
  useEffect(() => {
    if (!user) return;

    const loadAll = async () => {
      try {
        setLoading(true);

        // ÉTAPE 1 : Récupérer l'employé connecté
        const meRes = await api.auth.me();
        const currentUser = meRes.user || meRes;

        // Extraire le user_id (employee_id dans tasks = user_id)
        const userId = currentUser?.id;
        // Extraire l'employee_id pour les pointages/objectifs
        const employeeId = currentUser?.employee?.id
          || currentUser?.employee_id
          || currentUser?.id;

        console.log('👤 Employé connecté:', {
          userId,
          employeeId,
          prenom: currentUser.prenom
        });

        if (!employeeId) {
          console.error('❌ Pas d\'employee_id trouvé pour l\'utilisateur');
          setLoading(false);
          return;
        }

        // ÉTAPE 2 : Charger les données SPECIFIQUES à cet employé
        const [tasksRes, pointagesRes, objectifsRes, projetsRes] = await Promise.all([
          api.tasks.getAll({ employee_id: userId }),
          api.employees.historiquePointages(employeeId),
          api.objectifs.getAll({ employee_id: employeeId }),
          api.employees.projects(employeeId),
        ]);

        const empData = currentUser.employee || {};
        const prenom = currentUser.prenom || '';
        const nom = currentUser.nom || '';

        setE({
          id: employeeId,              // employee_id pour pointages/objectifs
          userId: userId,              // user_id pour les tâches
          prenom,
          nom,
          av: ((prenom[0] || '') + (nom[0] || '')).toUpperCase(),
          color: empData.color || '#C9A84C',
          poste: currentUser.poste || empData.poste || '—',
          dept: currentUser.departement?.nom || empData.departement?.nom || '—',
          filiale: currentUser.filiale?.nom || empData.filiale?.nom || '—',
          contrat: empData.contrat || 'CDI',
          entree: empData.date_entree ? new Date(empData.date_entree).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '—',
          limite: empData.heure_limite || '08:30',
          arrivee: empData.heure_arrivee || '—',
          present: !!empData.present,
          score: parseFloat(empData.score_performance) || 0,
          scoreLabel: empData.score_performance >= 9 ? 'Excellent' : empData.score_performance >= 8 ? 'Très bien' : empData.score_performance >= 7 ? 'Bien' : empData.score_performance >= 6 ? 'Satisfaisant' : 'À améliorer',
          // Ajout des propriétés manquantes utilisées par les composants
          tachesQuot: [],
          tachesHebdo: [],
          objectifs: [],
          pointage: [],
          criteres: [],
        });

        // Traiter les tâches de CET employé
        const tasksAll = (tasksRes.tasks || tasksRes.data || []).map(normalizeTask);
        
        console.log('📋 Tâches brutes de l\'API:', tasksAll);
        
        // Filtrer les tâches quotidiennes (toutes les tâches quotidiennes, pas seulement aujourd'hui)
        // Cela évite les problèmes de fuseau horaire et affiche toutes les tâches non terminées
        const tachesQuot = tasksAll.filter(t => !t._hebdo);  // Toutes les tâches non hebdomadaires
        
        console.log('✅ Tâches quotidiennes:', tachesQuot);
        
        setTachesQuot(tachesQuot);
        setTachesHebdo(tasksAll.filter(t => t._hebdo));

        // Traiter les pointages de CET employé
        const ptgData = pointagesRes.pointages || pointagesRes.data || pointagesRes.historique || [];
        setPointageHist(ptgData.slice(0, 7).map(normalizePointage));

        // Traiter les objectifs de CET employé
        const objData = objectifsRes.data || objectifsRes.objectifs || [];
        setObjectifs(objData.map(normalizeObjectif));

        // Critères depuis les données employé
        if (empData.criteres) {
          setCriteres(empData.criteres.map(normalizeCritere));
        }

        // Projets de l'employé
        const projData = projetsRes.projects || [];
        setProjets(projData);
      } catch (err) {
        console.error('❌ Erreur chargement TDB individuel:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [user]);

        
  const handleLogout = async () => {
    await authLogout();
    navigate('/auth', { replace: true });
  };

  const date = useMemo(() => new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), []);

    const addTask = useCallback(async (task) => {
    try {
      // Utiliser userId (user_id) pour les tâches, pas employeeId
      const taskEmployeeId = e?.userId || user?.id;
      
      if (!taskEmployeeId) {
        alert('Erreur : ID utilisateur non trouvé');
        console.error('❌ userId introuvable:', { e, user });
        return;
      }
      
      console.log('🚀 Tentative création tâche...');
      console.log('📝 Task data:', task);
      console.log('👤 User ID (pour tasks):', taskEmployeeId);
      
      const payload = {
        titre: task.titre,
        text: task.titre,
        priority: task.prio,
        priorite: task.prio,
        duration: task.duree,
        duree: task.duree,
        category: task.cat,
        categorie: task.cat,
        employee_id: taskEmployeeId,  // 🔥 user_id, pas employee_id
      };
      
      console.log('📤 Payload envoyé:', payload);
      
      const res = await api.tasks.create(payload);
      
      console.log('✅ Réponse API:', res);
      console.log('✅ Task créée:', res.task || res);
      
      const normalizedTask = normalizeTask(res.task || res);
      console.log('✅ Task normalisée:', normalizedTask);
      
      setTachesQuot(prev => {
        const newList = [...prev, normalizedTask];
        console.log('✅ Liste mise à jour:', newList.length, 'tâches');
        return newList;
      });
      
      console.log('✅ Tâche ajoutée avec succès !');
    } catch (err) {
      console.error('❌ Erreur création tâche:', err);
      console.error('❌ Détails erreur:', err.message, err.response);
      
      // NE PAS ajouter de fallback local pour voir l'erreur réelle
      alert(`Erreur lors de la création de la tâche: ${err.message}`);
    }
  }, [e, user]);

  const toggleTaskApi = useCallback(async (taskId, justif) => {
    try {
      await api.tasks.toggle(taskId);
      if (justif !== undefined) {
        await api.tasks.update(taskId, { description: justif });
      }
    } catch (err) {
      console.error('Erreur toggle task:', err);
    }
  }, []);

  const handleProgressionChange = useCallback(async (objectifId, progression, commentaire) => {
    const res = await api.objectifs.updateProgression(objectifId, progression, commentaire);
    const updated = res.objectif;
    setObjectifs(prev => prev.map(o =>
      (o.id === objectifId)
        ? { ...o, statut: updated.statut, pct: updated.progression, commentaire: updated.commentaire }
        : o
    ));
  }, []);

  if (loading || !e) {
    return (
      <div style={{ background: '#080e1a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A84C', fontSize: 14, fontFamily: "'DM Sans',sans-serif" }}>
        Chargement du tableau de bord...
      </div>
    );
  }

  // Construire un objet employé compatible avec les panels (avec tâches injectées)
  const empForPanels = {
    ...e,
    tachesQuot,
    tachesHebdo,
    objectifs,
    pointage: pointageHist.length > 0 ? pointageHist : [{ date: '—', arr: '—', dep: '—', statut: 'absent' }],
    criteres: criteres.length > 0 ? criteres : [
      { label: 'Ponctualité', val: 0, color: '#ef8080' },
      { label: 'Accomplissement tâches', val: 0, color: '#ef8080' },
      { label: 'Atteinte objectifs', val: 0, color: '#ef8080' },
      { label: 'Qualité du travail', val: 0, color: '#ef8080' },
      { label: 'Initiative & Proactivité', val: 0, color: '#ef8080' },
      { label: 'Travail en équipe', val: 0, color: '#ef8080' },
    ],
  };
  
  // Debug log
  console.log('📊 empForPanels construit:', {
    tachesQuotCount: tachesQuot.length,
    tachesHebdoCount: tachesHebdo.length,
    objectifsCount: objectifs.length
  });

  const sc = scol(e.score);
  const dayPct = pct(tachesQuot.filter(t => t.done).length, tachesQuot.length);
  const wkPct = pct(tachesHebdo.filter(t => t.done).length, tachesHebdo.length);
  const objAtteints = objectifs.filter(o => o.statut === 'atteint').length;
  const retards = pointageHist.filter(p => p.statut === 'retard').length;
  const ponct = pointageHist.length > 0 ? Math.round(pointageHist.filter(p => p.statut === 'ok').length / pointageHist.length * 100) : 0;

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: '#080e1a', color: '#fff', minHeight: '100vh', fontSize: 13 }}>
      {/* TOPBAR */}
      <div style={{ background: '#0d1825', borderBottom: '1px solid rgba(255,255,255,.06)', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 22px', position: 'sticky', top: 0, zIndex: 300 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,#C9A84C,#8a6028)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>👤</div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600 }}>FHC Groupe SARL — Mon Tableau de Bord</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,.3)' }}>Espace personnel · Suivi quotidien</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ fontSize: 10, color: '#445', background: '#111f2e', padding: '4px 10px', borderRadius: 18, border: '1px solid rgba(255,255,255,.06)' }}>{date}</div>
          <button onClick={() => setShowNewTask(true)} style={{ background: 'rgba(201,168,76,.12)', border: '1px solid rgba(201,168,76,.3)', borderRadius: 6, padding: '5px 13px', color: '#C9A84C', fontSize: 11, cursor: 'pointer' }}>+ Tâche</button>
          <button onClick={handleLogout} style={{ background: 'rgba(239,83,80,0.1)', border: '1px solid rgba(239,83,80,0.3)', borderRadius: 6, padding: '5px 13px', color: '#ef8080', fontSize: 11, cursor: 'pointer' }}>🔓 Déconnexion</button>
        </div>
      </div>

      {/* HERO */}
      <div style={{ padding: '24px 28px 0', background: `linear-gradient(180deg,${e.color}08 0%,transparent 100%)`, borderBottom: '1px solid rgba(255,255,255,.06)' }}>

        {/* Module de pointage */}
        <div style={{ marginBottom: 20 }}>
          <PointageModule currentUser={{ nom: `${e.prenom} ${e.nom}` }} />
          <div style={{ marginTop: 10 }}>
            <MarquerFinJournee onSuccess={() => {
              console.log('Fin de journée enregistrée - Dashboard Employé');
            }} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 20 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, border: `3px solid ${e.color}`, background: `${e.color}22`, color: e.color, flexShrink: 0 }}>{e.av}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600, marginBottom: 3 }}>{e.prenom} {e.nom}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.42)' }}>{e.poste} · {e.dept} · {e.filiale}</div>
            <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
              {[['📋', e.contrat], ['📅', `Depuis ${e.entree}`], ['⏰', `Limite : ${e.limite}`]].map(([ic, txt]) => (
                <span key={txt} style={{ fontSize: 10, color: '#667' }}>{ic} {txt}</span>
              ))}
              <span style={{ fontSize: 10, color: e.present ? '#66bb6a' : '#ef5350', fontWeight: 600 }}>
                {e.present ? `● Présent — ${e.arrivee}` : '● Absent aujourd\'hui'}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '14px 20px', background: 'rgba(255,255,255,.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 44, fontWeight: 600, lineHeight: 1, color: sc }}>{e.score}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.42)', marginTop: 2 }}>/10</div>
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: sc }}>{e.scoreLabel}</div>
          </div>
        </div>

        {/* HERO STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, paddingBottom: 20 }}>
          {[
            [dayPct + '%', 'Tâches du jour', pcol(dayPct)],
            [wkPct + '%', 'Tâches semaine', pcol(wkPct)],
            [`${objAtteints}/${objectifs.length}`, 'Objectifs atteints', objAtteints === objectifs.length ? '#66bb6a' : '#C9A84C'],
            [retards, 'Retards (7j)', retards === 0 ? '#66bb6a' : '#ef8080'],
            [ponct + '%', 'Ponctualité', ponct >= 80 ? '#66bb6a' : ponct >= 50 ? '#ffb74d' : '#ef8080'],
          ].map(([v, l, c]) => (
            <div key={l} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 9, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600, lineHeight: 1, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,.42)', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: 3, borderBottom: '1px solid rgba(255,255,255,.06)', padding: '0 28px', background: '#0d1825', position: 'sticky', top: 54, zIndex: 100, overflowX: 'auto' }}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '11px 15px', fontSize: 12, cursor: 'pointer', borderBottom: `2px solid ${activeTab === t.id ? '#C9A84C' : 'transparent'}`, color: activeTab === t.id ? '#C9A84C' : '#667', transition: 'all .15s', whiteSpace: 'nowrap' }}>
            {t.label}
          </div>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ padding: '24px 28px' }}>
        {activeTab === 'taches' && <TachesPanel e={empForPanels} setTachesQuot={setTachesQuot} toggleTaskApi={toggleTaskApi} />}
        {activeTab === 'semaine' && <SemainePanel e={empForPanels} />}
        {activeTab === 'objectifs' && <ObjectifsPanel e={empForPanels} onProgressionChange={handleProgressionChange} />}
        {activeTab === 'score' && <MaPerformance />}
        {activeTab === 'pointage' && <PointagePanel e={empForPanels} />}
        {activeTab === 'projets' && <ProjetsPanel projets={projets} />}
        {activeTab === 'permissions' && <MesDemandesPermission />}
        {activeTab === 'documents' && (() => { navigate('/documents'); return null; })()}
      </div>

      <ModalNewTask open={showNewTask} onClose={() => setShowNewTask(false)} onSave={(task) => { addTask(task); setShowNewTask(false); }} />
    </div>
  );
}
